/**
 * Card surface for the dashboard grid sections: white background, 8px radius,
 * soft shadow, centered section title, and an optional top-right "Know more"
 * link. Used only by the dashboard page.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface DashboardCardProps {
  title: string;
  href?: string;
  hrefLabel?: string;
  children?: ReactNode;
}

export default function DashboardCard({
  title,
  href,
  hrefLabel = 'Know More',
  children,
}: DashboardCardProps): JSX.Element {
  return (
    <div
      className="relative bg-white rounded-lg p-4"
      style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
    >
      {/* Centered section title — .section-title */}
      <h3
        className="m-0 mb-2 text-center font-semibold"
        style={{
          fontFamily: "var(--font-heading, 'Gill Sans MT', sans-serif)",
          fontSize: '1.1rem',
          color: 'var(--gray-800, #1F2937)',
        }}
      >
        {title}
      </h3>

      {/* Absolutely-positioned "Know More" button — .know-more-container */}
      {href && (
        <div className="absolute top-4 right-4">
          <Link
            to={href}
            className="inline-block font-medium cursor-pointer"
            style={{
              background: '#4EB7AC',
              color: '#ffffff',
              padding: '0.25rem 0.75rem',
              borderRadius: '15px',
              fontSize: '0.75rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#3aaa95';
              e.currentTarget.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
              e.currentTarget.style.textDecoration = 'none';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#4EB7AC';
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
            }}
          >
            {hrefLabel}
          </Link>
        </div>
      )}

      {/* Section content */}
      {children}
    </div>
  );
}
